'use client';

import React, { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import Script from 'next/script';

interface MetaPixelEvent {
  id?: string;
  name: string;
  type?: 'standard' | 'custom' | string;
  trigger?: 'page_view' | 'click' | 'form_submit' | string;
  isActive?: boolean;
}

interface MetaPixelProps {
  pixelId: string;
  events?: MetaPixelEvent[];
}

const LANDING_PATHS = new Set(['/', '/join-us']);

export default function MetaPixel({ pixelId, events = [] }: MetaPixelProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!pixelId) return;
    const fbq = typeof window !== 'undefined' ? (window as any).fbq : undefined;
    if (!fbq) return;

    // PageView is always re-fired on client-side route changes.
    // The base script already fires the first PageView at init; this covers
    // subsequent navigations.
    fbq('track', 'PageView');

    // Fire any admin-configured page_view events (ViewContent, etc.) so they
    // honour what was set in /admin/settings/analytics rather than being
    // silently dropped.
    const pageViewEvents = events.filter(
      e => e.isActive !== false && e.trigger === 'page_view' && e.name && e.name !== 'PageView'
    );

    const isLanding = LANDING_PATHS.has(pathname) || pathname.startsWith('/landing');
    const baseProps = { content_name: pathname, content_type: 'page' };

    for (const evt of pageViewEvents) {
      // ViewContent is meaningful on landing pages; other custom page-view
      // events fire on every route change.
      if (evt.name === 'ViewContent' && !isLanding) continue;
      const method = evt.type === 'custom' ? 'trackCustom' : 'track';
      fbq(method, evt.name, baseProps);
    }
  }, [pathname, searchParams, pixelId, events]);

  if (!pixelId) return null;

  return (
    <>
      <Script id="meta-pixel-base" strategy="afterInteractive">
        {`
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '${pixelId}');
          fbq('track', 'PageView');
        `}
      </Script>
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: 'none' }}
          src={`https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`}
          alt=""
        />
      </noscript>
    </>
  );
}
